import { IonIcon, IonRouterLink } from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import React, { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from '../../store/hooks';
import { setSelectedTrack } from '../../store/slices/track.slice';
import { openExternalLink } from '../../utils/openExternalLink';
import { useCanvas } from '../canvas/CanvasContext';
import { parseSourceHref } from './AgentChatHrefs';

export type AgentIcons = Record<string, string>;

export { parseSourceHref };
export type { SourceHit } from './AgentChatHrefs';

export const childText = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(childText).join('');
  if (React.isValidElement<{ children?: ReactNode }>(node)) {
    return childText(node.props.children);
  }
  return '';
};

const SOURCE_ONLY =
  /^(ch|your crate|example|serato|vdj|virtualdj|spotify|tidal|apple)?$/i;

export const AgentSourceLink: React.FC<{
  href?: string;
  icons: AgentIcons;
  inTable?: boolean;
  children?: ReactNode;
}> = ({ href, icons, inTable, children }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const canvas = useCanvas();
  const source = href ? parseSourceHref(href) : null;
  if (!source) {
    if (href && /^https?:/i.test(href)) {
      return (
        <a
          href={href}
          onClick={(event) => {
            event.preventDefault();
            openExternalLink(href);
          }}
        >
          {children}
        </a>
      );
    }
    return <span>{children}</span>;
  }

  const icon = icons[source.iconKey];
  const mark = icon ? (
    <img src={icon} alt="" className="agent-result-icon" />
  ) : null;
  const label = childText(children).trim();
  const iconOnly = Boolean(mark) && SOURCE_ONLY.test(label);
  const body = (
    <span
      className={`agent-result-hit${inTable ? ' is-row' : ''}${
        iconOnly ? ' is-source' : ''
      }`}
    >
      {mark}
      {iconOnly ? null : <span className="agent-result-title">{children}</span>}
    </span>
  );

  const trackId = source.trackId;
  if (trackId) {
    return (
      <a
        className="agent-result-link"
        title={source.label}
        href="#/track"
        onClick={(event) => {
          event.preventDefault();
          dispatch(
            setSelectedTrack({
              ID: trackId,
              Title: label,
              Artist: '',
            }),
          );
          history.push('/track');
        }}
      >
        {body}
      </a>
    );
  }

  // A call to action rather than a source chip: this one opens a working
  // surface, so it should not look like the tags that mark where a figure
  // came from. Away from the homepage there is no canvas to open into, so it
  // falls through to the routed page instead.
  const canvasTab = source.canvas;
  if (canvasTab && canvas) {
    return (
      <a
        className="agent-canvas-open"
        title={source.label}
        href={`#${source.to ?? ''}`}
        onClick={(event) => {
          event.preventDefault();
          canvas.openCanvas(canvasTab);
        }}
      >
        <IonIcon icon={arrowBack} className="agent-canvas-open-arrow" />
        {mark}
        <span className="agent-canvas-open-label">{children}</span>
      </a>
    );
  }

  if (!source.to) {
    return (
      <span className="agent-result-link is-static" title={source.label}>
        {body}
      </span>
    );
  }

  if (source.fromPage) {
    return (
      <a
        className="agent-result-link"
        title={source.label}
        href={`#${source.to}`}
        onClick={(event) => {
          event.preventDefault();
          history.push({
            pathname: source.to,
            state: { fromPage: source.fromPage },
          });
        }}
      >
        {body}
      </a>
    );
  }

  return (
    <IonRouterLink
      routerLink={source.to}
      routerDirection="forward"
      className="agent-result-link"
      title={source.label}
    >
      {body}
    </IonRouterLink>
  );
};
